import { SET_USER_ERROR } from 'shared/constants/errorTypes';
import { sendMessage, IFrameHelper } from 'widget/helpers/utils';
import { setHeader } from 'widget/helpers/axios';
import SseEventsConnection from 'widget/helpers/SseEventsConnection';
import { getAuthTokenCookieName } from 'shared/helpers/cookieHelpers';
import getConfig from 'widget/helpers/config';

import ContactsAPI from '../../api/contacts';

const state = {
  currentUser: {},
};

const SET_CURRENT_USER = 'SET_CURRENT_USER';
const CLEAR_CURRENT_USER = 'CLEAR_CURRENT_USER';

const parseErrorData = error =>
  error && error.response && error.response.data ? error.response.data : error;

export const getters = {
  getCurrentUser(_state) {
    return _state.currentUser;
  },
};

const setAuthHeader = (authToken) => {
  setHeader(authToken ? `Bearer ${authToken}` : undefined);
}

export const updateWidgetAuthToken = authToken => {
  setAuthHeader(authToken);

  if (IFrameHelper.isIFrame()) {
    sendMessage({
      event: 'setAuthCookie',
      data: { widgetAuthToken: authToken },
    });
  } else {
    const { widgetId } = getConfig().webChannel;

    localStorage.setItem(getAuthTokenCookieName({
      widgetId
    }), authToken);
  }
};

export const actions = {
  get: async ({ commit }) => {
    try {
      const { data } = await ContactsAPI.get();
      commit(SET_CURRENT_USER, data);
    } catch (error) {
      // Ignore error
    }
  },
  update: async ({ dispatch }, { user }) => {
    try {
      await ContactsAPI.update(user);
      dispatch('get');
    } catch (error) {
      // Ignore error
    }
  },
  createSession: async ({ dispatch, commit }, { authToken, tenantId, widgetId, user: userInput }) => {
    if (authToken) {
      setAuthHeader(authToken);
    }

    try {
      const {
        email,
        name,
        imageUrl,
        verificationHash,
        phoneNumber,
        companyName,
        city,
        countryCode,
        description,
        customAttributes,
        socialProfiles,
      } = userInput ?? {};

      dispatch('conversation/setConversationListLoading', true, { root: true });

      const { data: { authToken, sessionId, firestoreDocumentIdSuffix, messages, lastSeenAt, user } } = await ContactsAPI.createSession({
        tenantId,
        widgetId,
        user: {
          email,
          name,
          imageUrl,
          verificationHash,
          phoneNumber,
          additionalAttributes: {
            companyName,
            city,
            description,
            countryCode,
            socialProfiles,
          },
          customAttributes,
        }
      });

      updateWidgetAuthToken(authToken);

      if (window.sseEventsConnection) {
        window.sseEventsConnection.disconnect();
      }

      window.sseEventsConnection = new SseEventsConnection(
        window.UNTHREAD_WIDGET,
        tenantId,
        widgetId,
        sessionId,
        firestoreDocumentIdSuffix
      );

      commit(SET_CURRENT_USER, user);

      dispatch('conversation/setMetaUserLastSeenAt', lastSeenAt, { root: true });
      dispatch('conversation/clearConversations', {}, { root: true });
      dispatch('conversation/setMessagesInConversation', messages, { root: true });
      dispatch('conversation/setConversationListLoading', false, { root: true });

      sendMessage({
        event: 'sessionCreated',
        data: {},
      });
    } catch (error) {
      dispatch('conversation/setConversationListLoading', false, { root: true });

      const data = parseErrorData(error);

      sendMessage({ event: 'error', errorType: SET_USER_ERROR, data });
    }
  },
  destroySession: async () => {
    await ContactsAPI.destroySession();

    // NOTE: No need to clear out all the state because on stop the iframe gets reloaded.
  },
  setCustomAttributes: async (_, customAttributes = {}) => {
    try {
      await ContactsAPI.setCustomAttributes(customAttributes);
    } catch (error) {
      // Ignore error
    }
  },
  deleteCustomAttribute: async (_, customAttribute) => {
    try {
      await ContactsAPI.deleteCustomAttribute(customAttribute);
    } catch (error) {
      // Ignore error
    }
  },
};

export const mutations = {
  [SET_CURRENT_USER]($state, user) {
    const { currentUser } = $state;

    $state.currentUser = { ...currentUser, ...user };
  },
  [CLEAR_CURRENT_USER]($state) {
    $state.currentUser = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
